import { CustomButton } from '@/components';
import { abilityConstants, dashboardConstants, userCan } from '@/lib';
import {
  computeRequestFilters,
  SalesOperationsExport,
  SalesOperationsFilters,
  SalesOperationsFilterTags,
  SalesOperationsRepsTable,
  SalesOperationsStats,
  SummerDropdownFilter,
  defaultFilters,
  normalizeRequestFilters,
  FILTER_NAMES,
  FILTER_VALUES,
} from '@/modules/dashboard/components';
import {
  repStatusesSelector,
  requestExperienceOptionsAsync,
  requestRepStatusesAsync,
  requestApartmentStatusesAsync,
} from '@/redux/reps';
import { isEqual } from 'lodash-es';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { SearchBar } from '@/components/common';
import { requestSoRepsAsync } from '@/redux/sales-operations';
import { soRepsLoadingSelector } from '@/redux/loading';
import PropTypes from 'prop-types';
import { userRoleSelector, userSelector } from '@/redux/auth';
import { useRecruitSearchTypeOptions } from '@/hooks';
import { dataKeys } from '@/lib/adapters';
import {
  migrateHousingAsync,
  migrateRepsAsync,
} from '@/modules/AspynMigrator/redux';
import {
  migrateHousingLoadingSelector,
  migrateRepsLoadingSelector,
} from '@/redux/loading/selectors';
import { useFeatureFlag } from 'configcat-react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import classNames from 'classnames';

const {
  ACCESS_ASPYN_MIGRATOR_ABILITY,
} = abilityConstants;

const {
  ASPYN_MIGRATOR_FEATURE_FLAG_NAME,
} = dashboardConstants;

const { RECRUIT } = dataKeys;
const initialPage = 1;
const initialPageSize = 10;
const initialSearchType = RECRUIT;

const SalesOperations = ({
  repsLoading,
  requestExperienceOptions,
  requestApartmentStatuses,
  requestRepStatuses,
  getReps,
  role,
  repStatuses,
  migrateHousing,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFiltersRaw] = useState(defaultFilters);
  const [requestFilters, setRequestFilters] = useState(computeRequestFilters(defaultFilters));
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedPage, setSelectedPage] = useState(initialPage);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(initialSearchType);
  const [repsToChangeStatus, setRepsToChangeStatus] = useState([]);
  const [isOverrideMode, setIsOverrideMode] = useState(false);

  const user = useSelector(userSelector);

  const { value: isAspynMigratorEnabled } = useFeatureFlag(
    ASPYN_MIGRATOR_FEATURE_FLAG_NAME,
    false,
  );

  const dispatch = useDispatch();
  const isMigrateHousingLoading = useSelector(migrateHousingLoadingSelector);
  const isMigrateRepsLoading = useSelector(migrateRepsLoadingSelector);

  const searchTypeOptions = useRecruitSearchTypeOptions(role);

  const getRepsWithFilters = () => {
    const params = {
      page: {
        number: selectedPage,
        size: pageSize,
      },
      filter: {},
    };

    const normalizedFilters = normalizeRequestFilters(requestFilters);
    normalizedFilters.forEach(({ name, value }) => {
      params.filter[name] = value;
    });

    if (searchText) {
      params.filter.search = searchText;
      params.filter.search_type = searchType;
    }

    getReps(params);
    setRepsToChangeStatus([]);
  };

  useEffect(() => {
    getRepsWithFilters();
  }, [requestFilters, pageSize, searchText, searchType, selectedPage]);

  useEffect(() => {
    requestExperienceOptions();
    requestRepStatuses();
    requestApartmentStatuses();
  }, [requestExperienceOptions, requestRepStatuses, requestApartmentStatuses]);

  const onSearchClick = useCallback(({ searchText, searchType }) => {
    setSearchText(searchText);
    setSearchType(searchType);
    setSelectedPage(initialPage);
    setRepsToChangeStatus([]);
  }, []);

  const onPageChange = useCallback(({ selected }) => {
    setSelectedPage(selected);
    setRepsToChangeStatus([]);
  }, []);

  const setFilters = useCallback((nextFilters) => {
    const nextRequestFilters = computeRequestFilters(nextFilters);

    if (!isEqual(filters, nextFilters)) {
      setFiltersRaw(nextFilters);
    }

    if (!isEqual(requestFilters, nextRequestFilters)) {
      setRequestFilters(nextRequestFilters);
    }
  }, [filters, requestFilters]);

  const getFilterTagText = useCallback((name, value) => name === FILTER_NAMES.STATUS
    ? repStatuses.find((repStatus) => repStatus.statusCode === value)?.statusTitle
    : value, [repStatuses]);

  const toggleFiltersModal = useCallback(() => {
    setFiltersOpen(!filtersOpen);
  }, [filtersOpen]);

  const handlePushToAspyn = useCallback(() => {
    if (filters?.recruitingSeason !== FILTER_VALUES.DEFAULT) {
      migrateHousing({ recruitingSeasonId: filters.recruitingSeason });
    } else {
      migrateHousing();
    }
  }, [filters?.recruitingSeason, migrateHousing]);

  const handleMigrateReps = useCallback(() => {
    const userIds = repsToChangeStatus.map((rep) => rep.id);

    dispatch(migrateRepsAsync.request({
      userIds,
      ...(isOverrideMode ? { override: true } : {}),
    }));
    setRepsToChangeStatus([]);
  }, [dispatch, repsToChangeStatus, isOverrideMode]);

  return (
    <>
      <div className="px-8 py-3.5 bg-white border-b">
        <div className="flex items-center">
          <div className="shrink-0 text-gray-900 font-semibold">
            {dashboardConstants.SALES_OPERATIONS}
          </div>
          <SalesOperationsFilterTags
            filters={filters}
            setFilters={setFilters}
            getText={getFilterTagText}
          />
          <div className="flex ml-auto space-x-2">
            {isAspynMigratorEnabled && (
              <CustomButton
                color="white"
                disabled={isMigrateHousingLoading}
                onClick={handlePushToAspyn}
                className={`px-6 ${isMigrateHousingLoading ? 'text-gray-500' : 'text-gray-900'} font-normal`}
              >
                {isMigrateHousingLoading ? 'Pushing...' : 'Push Housing to Aspyn (Based on selected recruiting season)'}
              </CustomButton>
            )}
            <SummerDropdownFilter
              filters={filters}
              setFilters={setFilters}
            />
            <CustomButton
              color="white"
              onClick={toggleFiltersModal}
              className="px-6 text-gray-500 font-normal"
            >
              {dashboardConstants.FILTERS}
            </CustomButton>
            <SalesOperationsExport
              requestFilters={requestFilters}
              search={searchText}
              searchType={searchType}
            />
          </div>
        </div>
      </div>
      <div className="px-8">
        <SalesOperationsStats
          requestFilters={requestFilters}
          filters={filters}
          setFilters={setFilters}
          searchText={searchText}
          searchType={searchType}
        />
        <div className="mt-6 mb-7 flex justify-end items-center">
          {isAspynMigratorEnabled && userCan(user, ACCESS_ASPYN_MIGRATOR_ABILITY) && (
            <div className="relative inline-flex rounded-md shadow-sm mr-auto">
              <button
                type="button"
                onClick={handleMigrateReps}
                disabled={!repsToChangeStatus.length || isMigrateRepsLoading}
                className={classNames(
                  'inline-flex items-center justify-center py-2 px-4 rounded-l-md border text-base font-medium sm:text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                  !repsToChangeStatus.length || isMigrateRepsLoading
                    ? 'border-gray-300 bg-white text-gray-300 cursor-default'
                    : 'border-transparent bg-blue-600 text-white hover:bg-blue-700',
                )}
              >
                {isMigrateRepsLoading
                  ? 'Migrating...'
                  : `Migrate ${repsToChangeStatus.length > 1 ? `${repsToChangeStatus.length} reps` : 'rep'} to Aspyn${isOverrideMode ? ' (Override)' : ''}`}
              </button>
              <Menu as="div" className="relative -ml-px block">
                <Menu.Button
                  disabled={isMigrateRepsLoading}
                  className={classNames(
                    'inline-flex items-center py-2 px-2 rounded-r-md border border-l-0 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 h-full',
                    !repsToChangeStatus.length || isMigrateRepsLoading
                      ? 'border-gray-300 bg-white text-gray-300 cursor-default'
                      : 'border-blue-700 bg-blue-600 text-white hover:bg-blue-700',
                  )}
                >
                  <span className="sr-only">Open options</span>
                  <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-1/2 -translate-x-1/2 z-20 mt-2 w-60 origin-top rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setIsOverrideMode(false)}
                            className={classNames(
                              active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                              'flex items-start justify-between w-full px-4 py-2 text-left text-sm',
                            )}
                          >
                            <div>
                              <div className="font-medium">Default</div>
                              <div className="text-xs text-gray-500">Normal migration</div>
                            </div>
                            {!isOverrideMode && (
                              <CheckIcon className="w-4 h-4 text-blue-600 mt-0.5 ml-2 shrink-0" aria-hidden="true" />
                            )}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setIsOverrideMode(true)}
                            className={classNames(
                              active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                              'flex items-start justify-between w-full px-4 py-2 text-left text-sm',
                            )}
                          >
                            <div>
                              <div className="font-medium">With Override</div>
                              <div className="text-xs text-gray-500">Overrides existing data in Aspyn</div>
                            </div>
                            {isOverrideMode && (
                              <CheckIcon className="w-4 h-4 text-blue-600 mt-0.5 ml-2 shrink-0" aria-hidden="true" />
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          )}
          <SearchBar
            searchType={searchType}
            searchText={searchText}
            typeOptions={searchTypeOptions}
            inputName={dashboardConstants.REP_SEARCH_NAME}
            selectName={dashboardConstants.REP_TYPE_FILTER_NAME}
            disabled={Boolean(repsLoading)}
            onSearchClick={onSearchClick}
            isSearchTypeSupported={!!searchTypeOptions.length}
          />
        </div>
        <SalesOperationsRepsTable
          getReps={getRepsWithFilters}
          pageSize={pageSize}
          setPageSize={setPageSize}
          onPageChange={onPageChange}
          selectedPage={selectedPage}
          initialPage={initialPage}
          recruitingSeasonId={filters?.recruitingSeason}
          repsToChangeStatus={repsToChangeStatus}
          setRepsToChangeStatus={setRepsToChangeStatus}
        />
      </div>
      <SalesOperationsFilters
        modalOpen={filtersOpen}
        closeModal={toggleFiltersModal}
        filters={filters}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
    </>
  );
};

SalesOperations.propTypes = {
  repsLoading: PropTypes.bool,
  getReps: PropTypes.func,
  requestExperienceOptions: PropTypes.func,
  requestApartmentStatuses: PropTypes.func,
  requestRepStatuses: PropTypes.func,
  role: PropTypes.string,
  repStatuses: PropTypes.arrayOf(PropTypes.object),
  migrateHousing: PropTypes.func,
};

const mapStateToProps = (state) => ({
  repsLoading: soRepsLoadingSelector(state),
  role: userRoleSelector(state),
  repStatuses: repStatusesSelector(state),
});

const mapDispatchToProps = {
  getReps: requestSoRepsAsync.request,
  requestExperienceOptions: requestExperienceOptionsAsync.request,
  requestApartmentStatuses: requestApartmentStatusesAsync.request,
  requestRepStatuses: requestRepStatusesAsync.request,
  migrateHousing: migrateHousingAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesOperations);
